import { Ionicons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { Text } from '@/components/ui';
import { Modal } from '@/components/ui/modal';
import { useAssociationDues, useGlCodes, useInvalidateAccounting } from '@/hooks/use-accounting';
import { accountingService } from '@/services/accounting-service';
import { type AssessmentCadence, type GlCode } from '@/types/models';
import { periodsPerYear } from '@/utils/accounting-dues';
import { dollarsToCents, pickAndParseFiles } from '@/utils/accounting-export';
import { classifyTemplateCsv, CSV_TEMPLATES, downloadCsvTemplate, normalizeRow } from '@/utils/csv-templates';
import { formatMoneyCents } from '@/utils/format-money';

interface Props {
	associationId: string;
	cadence?: AssessmentCadence;
	onClose: () => void;
}

type Phase = 'pick' | 'matching' | 'preview' | 'importing' | 'done';

/** One GL code's annual budget, resolved against the global chart. */
interface BudgetDraft {
	glCode: string;
	glName: string;
	glCodeId: string;
	annualCents: number;
}

/**
 * Import an association's annual budget as one row per GL code.
 *
 * The rows become `association_dues_lines`, whose amounts are ANNUAL figures in
 * par-value mode and whose total is the annual operating budget. That is the
 * same table the dues posting already fans charges out across, so an import
 * configures the billing basis and the GL split in one step.
 *
 * Re-importing REPLACES the set: existing lines are removed first, so the file
 * is the whole truth rather than something that accumulates on re-upload.
 */
export const ImportBudgetModal: React.FC<Props> = ({ associationId, cadence, onClose }) => {
	const [phase, setPhase] = useState<Phase>('pick');
	const [drafts, setDrafts] = useState<BudgetDraft[]>([]);
	const [unmatched, setUnmatched] = useState<Array<{ code: string; reason: string }>>([]);
	const [fileSummary, setFileSummary] = useState<Array<{ name: string; label: string; ok: boolean }>>([]);
	const [progressLabel, setProgressLabel] = useState('');
	const [importErrors, setImportErrors] = useState<string[]>([]);
	const [replaced, setReplaced] = useState(0);
	/** What actually persisted — NOT what the file asked for. */
	const [saved, setSaved] = useState({ cents: 0, lines: 0 });

	const { data: glResp } = useGlCodes({ limit: 1000, activeOnly: true });
	const { data: dues } = useAssociationDues(associationId);
	const invalidate = useInvalidateAccounting();

	const totalCents = useMemo(() => drafts.reduce((s, d) => s + d.annualCents, 0), [drafts]);
	const existingLineCount = dues?.lines?.length ?? 0;

	const pick = async () => {
		const files = await pickAndParseFiles();
		if (files.length === 0) return;
		setPhase('matching');

		const glRows = (glResp?.data ?? []) as GlCode[];
		const byCode = new Map(glRows.map((g) => [g.code.trim(), g]));

		const next: BudgetDraft[] = [];
		const bad: Array<{ code: string; reason: string }> = [];
		const summary: Array<{ name: string; label: string; ok: boolean }> = [];

		for (const f of files) {
			const kind = classifyTemplateCsv(f.rows);
			if (kind !== 'budget') {
				summary.push({
					name: f.name,
					label: kind ? `${CSV_TEMPLATES[kind].title} — not the annual budget template` : 'not recognized — download the template below',
					ok: false,
				});
				continue;
			}
			summary.push({ name: f.name, label: `Annual budget · ${f.rows.length} rows`, ok: true });
			for (const raw of f.rows) {
				const r = normalizeRow(raw);
				if (!r.gl_code) continue;
				const cents = r.annual_amount ? dollarsToCents(r.annual_amount) : null;
				if (cents == null) {
					bad.push({ code: r.gl_code, reason: `unreadable amount "${r.annual_amount}"` });
					continue;
				}
				if (cents <= 0) {
					bad.push({ code: r.gl_code, reason: 'amount must be greater than zero' });
					continue;
				}
				// An unknown GL code BLOCKS rather than being audited under a success
				// banner — a typo'd code would otherwise drop that slice of the budget
				// and quietly under-bill every owner.
				const gl = byCode.get(r.gl_code.trim());
				if (!gl) {
					bad.push({ code: r.gl_code, reason: 'no such GL code in the chart of accounts' });
					continue;
				}
				next.push({ glCode: gl.code, glName: gl.fullPath || gl.name, glCodeId: gl.id, annualCents: cents });
			}
		}

		setDrafts(next);
		setUnmatched(bad);
		setFileSummary(summary);
		setPhase('preview');
	};

	const runImport = async () => {
		setPhase('importing');
		const errors: string[] = [];
		let removed = 0;

		// Replace, don't accumulate: the file is the whole budget.
		//
		// A failed delete must ABORT before anything is created. The annual budget
		// is SUM(amount_cents) over these lines, so one surviving old row would be
		// added to the full imported set — silently inflating the budget and every
		// owner's dues. Leaving the previous budget intact is the safe outcome; the
		// import is re-runnable.
		const existing = dues?.lines ?? [];
		for (const line of existing) {
			setProgressLabel(`Clearing the previous budget ${removed + 1}/${existing.length}…`);
			try {
				await accountingService.deleteDuesLine(associationId, line.id);
				removed++;
			} catch (e) {
				await invalidate();
				setReplaced(removed);
				setImportErrors([
					`Could not remove the previous budget line ${line.label ?? line.id}: ${(e as Error).message}`,
					removed > 0
						? `Stopped before importing. ${removed} of ${existing.length} previous line${existing.length === 1 ? '' : 's'} were removed — the budget is incomplete, so re-run the import to finish replacing it.`
						: 'Stopped before importing. The previous budget is untouched — re-run the import.',
				]);
				setPhase('done');
				return;
			}
		}

		// Stop at the FIRST create failure. Continuing would leave a partial
		// budget, which under-bills every owner proportionally on the next dues
		// run — and the account pages would show the short amount as if it were
		// correct. Better to stop and report exactly what landed.
		let savedCents = 0;
		let savedLines = 0;
		for (let i = 0; i < drafts.length; i++) {
			const d = drafts[i];
			setProgressLabel(`Saving ${i + 1}/${drafts.length} — ${d.glCode}…`);
			try {
				await accountingService.createDuesLine(associationId, {
					glCodeId: d.glCodeId,
					label: d.glName,
					amountCents: d.annualCents,
					sortOrder: i,
				});
				savedCents += d.annualCents;
				savedLines++;
			} catch (e) {
				errors.push(`${d.glCode}: ${(e as Error).message}`);
				errors.push(
					`Stopped after ${savedLines} of ${drafts.length} lines. The annual budget is now`
					+ ` ${formatMoneyCents(savedCents)} instead of ${formatMoneyCents(totalCents)} — every owner would be`
					+ ' under-billed until this is fixed. Re-run the import.',
				);
				break;
			}
		}

		await invalidate();
		setReplaced(removed);
		// Report what PERSISTED, never the file's intent — a green banner showing
		// the CSV total while lines failed to save is how a short budget goes out
		// unnoticed.
		setSaved({ cents: savedCents, lines: savedLines });
		setImportErrors(errors);
		setPhase('done');
	};

	const canImport = drafts.length > 0 && unmatched.length === 0;

	return (
		<Modal visible onDismiss={onClose} contentContainerStyle={styles.modal}>
			<View style={styles.header}>
				<Text style={styles.title}>Import Annual Budget</Text>
				<Pressable onPress={onClose} hitSlop={10}><Text style={styles.close}>✕</Text></Pressable>
			</View>
			<ScrollView contentContainerStyle={styles.body}>
				<Text style={styles.hint}>
					One row per GL code with that code&apos;s annual budget. The total becomes this association&apos;s
					annual operating budget; in par-value mode each owner is billed their ownership percentage of it,
					split across these codes. Importing replaces the current budget.
				</Text>

				<View style={styles.templatesBox}>
					<Text style={styles.templatesTitle}>CSV template (with sample rows)</Text>
					<Pressable style={styles.templateRow} onPress={() => downloadCsvTemplate('budget')}>
						<Ionicons name="download-outline" size={16} color="#1976d2" />
						<View style={{ flex: 1 }}>
							<Text style={styles.templateName}>{CSV_TEMPLATES.budget.title} — {CSV_TEMPLATES.budget.filename}</Text>
							<Text style={styles.templateDesc}>{CSV_TEMPLATES.budget.description}</Text>
						</View>
					</Pressable>
				</View>

				{phase === 'pick' && (
					<Pressable style={styles.primary} onPress={pick}>
						<Text style={styles.primaryLabel}>Pick CSV file…</Text>
					</Pressable>
				)}

				{phase === 'matching' && <Text style={styles.note}>Parsing + matching GL codes…</Text>}

				{(phase === 'preview' || phase === 'done') && (
					<View style={styles.previewBox}>
						{fileSummary.map((f, i) => (
							<PreviewRow key={i} label={f.name} value={f.label} tone={f.ok ? undefined : 'red'} />
						))}
						<View style={styles.previewDivider} />
						{drafts.map((d) => (
							<PreviewRow key={d.glCodeId} label={`${d.glCode} — ${d.glName}`} value={formatMoneyCents(d.annualCents)} />
						))}
						{drafts.length > 0 && (
							<>
								<View style={styles.previewDivider} />
								<PreviewRow label="Annual operating budget" value={formatMoneyCents(totalCents)} />
								<PreviewRow
									label={`Per ${cadence ?? 'monthly'} period, all owners`}
									value={formatMoneyCents(Math.round(totalCents / periodsPerYear(cadence)))}
								/>
							</>
						)}
						{existingLineCount > 0 && phase === 'preview' && (
							<PreviewRow label="Existing lines to replace" value={String(existingLineCount)} tone="red" />
						)}
					</View>
				)}

				{phase === 'preview' && unmatched.length > 0 && (
					<View style={styles.warnBox}>
						<Text style={styles.warnTitle}>Unrecognized rows — nothing will be imported</Text>
						{unmatched.slice(0, 8).map((u, i) => (
							<Text key={i} style={styles.warnLine}>&ldquo;{u.code}&rdquo; — {u.reason}</Text>
						))}
						{unmatched.length > 8 && <Text style={styles.warnLine}>…and {unmatched.length - 8} more</Text>}
						<Text style={styles.warnNote}>
							Importing a partial budget would under-bill every owner, so fix these rows (or add the GL
							codes to the chart of accounts) and re-upload.
						</Text>
					</View>
				)}

				{phase === 'preview' && (
					<View style={{ flexDirection: 'row', gap: 8 }}>
						<Pressable style={styles.secondary} onPress={() => { setDrafts([]); setUnmatched([]); setFileSummary([]); setPhase('pick'); }}>
							<Text style={styles.secondaryLabel}>Reset</Text>
						</Pressable>
						<Pressable
							style={[styles.primary, { flex: 1, opacity: canImport ? 1 : 0.5 }]}
							onPress={runImport}
							disabled={!canImport}
						>
							<Text style={styles.primaryLabel}>
								Import {drafts.length} GL line{drafts.length === 1 ? '' : 's'}
							</Text>
						</Pressable>
					</View>
				)}

				{phase === 'importing' && (
					<View style={styles.resultBox}>
						<Text style={styles.resultOk}>{progressLabel || 'Importing…'}</Text>
					</View>
				)}

				{phase === 'done' && (
					<>
						{/* Green ONLY when the whole file landed. A success banner next to
						    an error box is how a short budget ships unnoticed. */}
						<View style={importErrors.length > 0 ? styles.failBox : styles.resultBox}>
							{importErrors.length === 0 ? (
								<>
									<Text style={styles.resultOk}>
										✓ Annual budget set to {formatMoneyCents(saved.cents)} across {saved.lines} GL code{saved.lines === 1 ? '' : 's'}
									</Text>
									{replaced > 0 && <Text style={styles.resultLine}>{replaced} previous line{replaced === 1 ? '' : 's'} replaced</Text>}
									<Text style={styles.note}>
										Each owner&apos;s dues are recalculated from this budget the next time dues are posted —
										check the Post Dues preview before posting.
									</Text>
								</>
							) : (
								<>
									<Text style={styles.resultFail}>Import did not complete</Text>
									<Text style={styles.resultLine}>
										Annual budget is now {formatMoneyCents(saved.cents)} across {saved.lines} GL
										code{saved.lines === 1 ? '' : 's'} — the file specified {formatMoneyCents(totalCents)}.
									</Text>
									<Text style={styles.note}>
										Do NOT post dues until this is resolved: par-value dues divide this budget, so every
										owner would be billed short. Re-running the import is safe.
									</Text>
								</>
							)}
						</View>
						{importErrors.length > 0 && (
							<View style={styles.errorsBox}>
								{importErrors.slice(0, 20).map((e, i) => <Text key={i} style={styles.errorLine}>{e}</Text>)}
							</View>
						)}
						<Pressable style={styles.secondary} onPress={onClose}>
							<Text style={styles.secondaryLabel}>Close</Text>
						</Pressable>
					</>
				)}
			</ScrollView>
		</Modal>
	);
};

const PreviewRow: React.FC<{ label: string; value: string; tone?: 'red' | 'green' }> = ({ label, value, tone }) => (
	<View style={styles.previewRow}>
		<Text style={styles.previewLabel} numberOfLines={1}>{label}</Text>
		<Text style={[styles.previewCount, tone === 'red' && { color: '#c62828' }, tone === 'green' && { color: '#2e7d32' }]}>{value}</Text>
	</View>
);

const styles = StyleSheet.create({
	modal: { width: '92%', maxWidth: 620, maxHeight: '90%', borderRadius: 12, padding: 0 },
	header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#eee' },
	title: { fontSize: 18, fontWeight: '700' },
	close: { fontSize: 20, color: '#888', paddingHorizontal: 8 },
	body: { padding: 20, gap: 12 },
	hint: { color: '#546e7a', fontSize: 13, lineHeight: 19 },
	templatesBox: { backgroundColor: '#f5f7fa', borderRadius: 8, padding: 12, gap: 10 },
	templatesTitle: { fontSize: 11, fontWeight: '700', color: '#90a4ae', textTransform: 'uppercase', letterSpacing: 0.5 },
	templateRow: { flexDirection: 'row', gap: 8, alignItems: 'flex-start' },
	templateName: { color: '#1976d2', fontWeight: '600', fontSize: 13 },
	templateDesc: { color: '#78909c', fontSize: 11, lineHeight: 15, marginTop: 1 },
	primary: { backgroundColor: '#007AFF', padding: 12, borderRadius: 6, alignItems: 'center' },
	primaryLabel: { color: '#fff', fontWeight: '600' },
	secondary: { backgroundColor: '#eceff1', padding: 12, borderRadius: 6, alignItems: 'center', paddingHorizontal: 16 },
	secondaryLabel: { color: '#37474f', fontWeight: '500' },
	previewBox: { backgroundColor: '#f5f7fa', borderRadius: 8, padding: 12 },
	previewRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4, gap: 12 },
	previewLabel: { color: '#455a64', flex: 1 },
	previewCount: { color: '#212121', fontWeight: '700' },
	previewDivider: { borderTopWidth: 1, borderTopColor: '#e0e6ea', marginVertical: 6 },
	note: { color: '#78909c', fontSize: 12, lineHeight: 17, marginTop: 6 },
	warnBox: { backgroundColor: '#fff8e1', borderLeftWidth: 3, borderLeftColor: '#fbc02d', borderRadius: 6, padding: 12, gap: 4 },
	warnTitle: { fontWeight: '700', color: '#795548', marginBottom: 4 },
	warnLine: { color: '#5d4037', fontSize: 12 },
	warnNote: { color: '#795548', fontSize: 12, marginTop: 6, fontStyle: 'italic' },
	resultBox: { padding: 12, backgroundColor: '#e8f5e9', borderRadius: 6, gap: 4 },
	failBox: { padding: 12, backgroundColor: '#fdecea', borderRadius: 6, gap: 4, borderLeftWidth: 3, borderLeftColor: '#c62828' },
	resultOk: { color: '#2e7d32', fontWeight: '600' },
	resultFail: { color: '#c62828', fontWeight: '700' },
	resultLine: { color: '#37474f', fontSize: 13 },
	errorsBox: { padding: 10, backgroundColor: '#fff3e0', borderRadius: 6, borderLeftWidth: 3, borderLeftColor: '#fbc02d' },
	errorLine: { color: '#795548', fontSize: 12, paddingVertical: 1 },
});
